import React from 'react';
import { Alert, Box, Typography } from '@mui/material';
import {
  Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import ResponsiveDataGrid from '../common/datagrid/ResponsiveDataGrid';
import TotalsLine from '../common/list/TotalsLine';

const CHART_COLORS = ['#1976d2', '#2e7d32', '#ef6c00', '#6a1b9a', '#c62828'];

// 6번(Fable 재검토 2026-10-26): 백엔드가 금액/숫자를 더 이상 문자열로 안 보내고(엑셀 numFmt가
// 안 먹던 문제) 숫자 그대로 보낸다 - 숫자형 값도 그대로 받아 표시만 ,로 묶는다. 과거 데이터 모양
// (문자열 숫자)도 혹시 섞여 있으면 그대로 처리할 수 있게 문자열 분기도 남겨 둔다.
const formatNumberIfNumeric = (v) => {
  if (v === null || v === undefined || v === '') return v;
  if (typeof v === 'number') return v.toLocaleString();
  const n = Number(v);
  return Number.isFinite(n) && String(v).match(/^-?\d+(\.\d+)?$/) ? n.toLocaleString() : v;
};

const isSummaryResult = (columns) => columns.some((c) => /^(g|m)\d+$/.test(c.alias));

/** 표 또는 차트(막대/선) - 그룹 1~2개 + 집계 1~2개일 때만 차트를 고를 수 있다(계획 §6-2). */
const ResultView = ({ result, viewTyp = 'TABLE', gridId = 'report-result' }) => {
  if (!result) return null;
  const { columns = [], rows = [], total = 0, limited, omittedFieldLabels } = result;

  const gridColumns = columns.map((c) => ({
    field: c.alias,
    headerName: c.label,
    flex: 1,
    minWidth: 110,
    valueFormatter: (value) => formatNumberIfNumeric(value),
  }));
  const gridRows = rows.map((r, i) => ({ id: i, ...r }));

  // 6번: 요약형(summary)의 __TOTAL은 COUNT(*) OVER()가 GROUP BY 뒤에 계산돼 "그룹 수"다 - "건수"로
  // 보이면 실제 데이터 건수(행수 합)로 오해한다(계획 §4-4가 원래 의도한 그대로). 라벨을
  // "그룹 N개"로 바꾸고, COUNT(*) 집계(라벨 "건수")가 있으면 그 열의 합을 따로 보여준다(행이
  // 화면에 받아온 만큼만 있어도 SUM은 화면에서 바로 계산하니 cheap하다).
  const isSummary = isSummaryResult(columns);
  const countCol = columns.find((c) => c.label === '건수');
  const totalsItems = [];
  if (isSummary) {
    totalsItems.push({ label: '그룹', value: `${total.toLocaleString()}개` });
    if (countCol) {
      const sumCount = rows.reduce((acc, r) => acc + (Number(r[countCol.alias]) || 0), 0);
      totalsItems.push({ label: '전체 건수', value: `${sumCount.toLocaleString()}건` });
    }
  } else {
    totalsItems.push({ label: '건수', value: total.toLocaleString() });
  }
  if (limited) {
    totalsItems.push({ label: '안내', value: `상위 ${rows.length.toLocaleString()}건만 표시`, color: 'warning.main' });
  }

  const categoryCol = columns[0];
  const seriesCols = columns.slice(1).filter((c) => c.dataTyp === 'NUM' || c.dataTyp === 'MONEY');

  return (
    <Box>
      {omittedFieldLabels?.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          권한이 없는 항목 {omittedFieldLabels.length}개를 빼고 보여줍니다: {omittedFieldLabels.join(', ')}
        </Alert>
      )}
      <TotalsLine items={totalsItems} sx={{ justifyContent: 'flex-start', mb: 1 }} />

      {viewTyp === 'TABLE' || !categoryCol || seriesCols.length === 0 ? (
        <Box sx={{ height: 440, width: '100%' }}>
          {/* 9번(Fable 재검토 2026-10-26): MUI 기본 CSV 내보내기를 끈다 - RUN_SRC=EXPORT 감사
              로그·서버 상한(F2)·숫자 서식을 거치지 않는 두 번째 내보내기 경로였다. 엑셀은
              보기/목록 화면의 "엑셀" 버튼(exportReportToExcel, RUN_SRC=EXPORT)만 쓴다. */}
          <ResponsiveDataGrid
            gridId={gridId}
            rows={gridRows}
            columns={gridColumns}
            disableRowSelectionOnClick
            slotProps={{ toolbar: { showExport: true } }}
          />
        </Box>
      ) : (
        <Box sx={{ height: 400, width: '100%' }}>
          <ResponsiveContainer width="100%" height="100%">
            {viewTyp === 'LINE' ? (
              <LineChart data={gridRows}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey={categoryCol.alias} />
                <YAxis />
                <Tooltip />
                <Legend />
                {seriesCols.map((c, i) => (
                  <Line key={c.alias} type="monotone" dataKey={c.alias} name={c.label} stroke={CHART_COLORS[i % CHART_COLORS.length]} />
                ))}
              </LineChart>
            ) : (
              <BarChart data={gridRows}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey={categoryCol.alias} />
                <YAxis />
                <Tooltip />
                <Legend />
                {seriesCols.map((c, i) => (
                  <Bar key={c.alias} dataKey={c.alias} name={c.label} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                ))}
              </BarChart>
            )}
          </ResponsiveContainer>
        </Box>
      )}
      {rows.length === 0 && (
        <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>결과가 없습니다.</Typography>
      )}
    </Box>
  );
};

export default ResultView;
